import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { orgPath, useLicApi } from './licApi';
import { EmptyRow, LoadingBox, MUTED, ViewHeader, tableContainerSx } from './OrgCommon';
import { formatAction, formatDateTime, formatDetails, formatRelative } from './orgFormat';

const PAGE_SIZE = 50;

/** The org's audit trail, newest first, paged with `nextBefore`. */
const ActivityView = ({ org }) => {
  const { licFetch } = useLicApi();
  const [events, setEvents] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  const fetchPage = useCallback(
    (before) => licFetch(orgPath(org.id, '/events'), { query: { before, limit: PAGE_SIZE } }),
    [licFetch, org.id]
  );

  const loadFirst = useCallback(async () => {
    setError('');
    try {
      const result = await fetchPage();
      setEvents(Array.isArray(result.events) ? result.events : []);
      setNextBefore(result.nextBefore || null);
    } catch (loadError) {
      if (loadError.status !== 401) setError(loadError.message);
    }
  }, [fetchPage]);

  useEffect(() => {
    loadFirst().finally(() => setLoading(false));
  }, [loadFirst]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadFirst();
    setRefreshing(false);
  };

  const handleLoadMore = async () => {
    if (!nextBefore) return;
    setLoadingMore(true);
    setError('');
    try {
      const result = await fetchPage(nextBefore);
      const page = Array.isArray(result.events) ? result.events : [];
      setEvents((current) => {
        const seen = new Set(current.map((event) => event.id).filter(Boolean));
        return [...current, ...page.filter((event) => !event.id || !seen.has(event.id))];
      });
      setNextBefore(result.nextBefore || null);
    } catch (loadError) {
      if (loadError.status !== 401) setError(loadError.message);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <Box>
      <ViewHeader title="Activity" onRefresh={handleRefresh} refreshing={refreshing} />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <LoadingBox />
      ) : (
        <>
          <Box sx={tableContainerSx}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.length === 0 ? (
                  <EmptyRow colSpan={5}>No activity yet.</EmptyRow>
                ) : (
                  events.map((event, index) => {
                    const details = formatDetails(event.details);
                    return (
                      <TableRow key={event.id || `${event.atUtc}-${index}`} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                          <Tooltip title={formatRelative(event.atUtc)}>
                            <span>{formatDateTime(event.atUtc)}</span>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          {formatAction(event.action)}
                          {details && (
                            <Typography variant="caption" component="div" sx={{ color: MUTED, wordBreak: 'break-word' }}>
                              {details}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{event.email || '—'}</TableCell>
                        <TableCell>{event.deviceName || '—'}</TableCell>
                        <TableCell sx={{ wordBreak: 'break-all' }}>{event.actor || '—'}</TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </Box>
          {nextBefore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button variant="outlined" onClick={handleLoadMore} disabled={loadingMore} sx={{ textTransform: 'none' }}>
                {loadingMore ? <CircularProgress size={20} /> : 'Load more'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ActivityView;
